'use client';

import { styled } from 'styled-components';
import cs from 'classnames';
import { WistiaChannel } from '@wistia/react-embeds';

type ChannelProps = {
  /** 10 character hash ID of the media */
  hashedId: string;

  /**
   * Embed presentation: `inline` renders the channel in the page flow;
   * `popover` renders a preview card that launches the full channel on click.
   */
  mode?: 'inline' | 'popover';

  /**
   * How the channel’s own background is handled. Defaults to `transparent`.
   *
   * A channel carries one background from the Wistia app, but the same channel
   * can be embedded on pages that do not share it, so this is set per embed
   * rather than read from the channel.
   *
   * `transparent` clears it so the embed blends into the surrounding `Section`.
   * The embed derives its text colors from the background set on the channel in
   * the Wistia app, so this only stays legible where the `Section` and that
   * setting agree — a channel set to a dark background needs a dark `Section`.
   * Use it on designed pages, where both ends are known.
   *
   * `channel` keeps the background the channel was given in the app. Use it
   * wherever that match cannot be guaranteed — anything hosting channels chosen
   * by an author rather than a designer, such as `BlogChannel`.
   */
  background?: 'transparent' | 'channel';

  /** Optional class name, nesc. for extending as a styled-component */
  className?: string;
};

const ChannelWrapper = styled.div`
  overflow: hidden;

  /*
   * A popover renders a preview card whose own height is 100% of the embed
   * container (.wistia_channel). That container has no intrinsic height, so the
   * card collapses to zero and renders empty. Give the container a responsive
   * box instead of fixed pixels: full width so the card tracks its parent column
   * (and never overflows on narrow viewports), and a 3:2 aspect-ratio so the
   * height scales with that width. Inline embeds size themselves from their
   * gallery content and need none of this.
   */
  &.popover .wistia_channel {
    width: 100%;
    aspect-ratio: 3 / 2;
  }

  /*
   * These selectors reach into markup the embed script renders, which this
   * component does not own. The script assigns both backgrounds inline, so
   * !important is the only way a stylesheet can clear them — this is not
   * specificity escalation against one of our own globals.
   *
   * TODO: Remove this short-term work-around when product is updated to provide
   * in-channel transparent embeds. The channel's backgroundColor option accepts
   * only dark/light/brandDark/brandLight/hex, and the react-embeds wrapper does
   * not forward it to the embed at all.
   */
  &.transparent .w-gallery-view {
    background: none !important;
  }

  &.transparent .w-gallery-view__section,
  &.transparent .w-gallery-view__gallery-card-cover {
    background-image: none !important;
  }
`;

const Channel = ({
  hashedId,
  mode = 'inline',
  background = 'transparent',
  className,
}: ChannelProps) => {
  return (
    <ChannelWrapper
      className={cs(className, {
        popover: mode === 'popover',
        transparent: background === 'transparent',
      })}
    >
      <WistiaChannel hashedId={hashedId} mode={mode} />
    </ChannelWrapper>
  );
};

export default Channel;
